import type { ParseKeys } from 'i18next'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { cx, formatDateTime } from '@/shared/lib'
import { Icon, type IconName } from '@/shared/ui/Icon'
import { StatusBadge } from '@/shared/ui/StatusBadge'
import { describeNotification, type InvoiceChangeView } from '../lib/notificationView'
import type { NotificationItem, NotificationKind } from '../model/types'
import s from './NotificationItemCard.module.css'

export type NotificationItemCardProps = {
  item: NotificationItem
  /** Действие справа: «Отметить прочитанным» на странице. В ленте колокольчика — пусто. */
  action?: ReactNode
}

type CodedKind = Exclude<NotificationKind, 'legacy.message'>

/** Глиф по виду события. Смысл несёт текст; иконка — ориентир, декоративна. */
const KIND_ICON: Record<NotificationKind, IconName> = {
  'manager.reserve.placed': 'box',
  'manager.invoice.appended': 'doc',
  'manager.invoice.changed': 'edit',
  'client.invoice.changed': 'edit',
  'reserve.invoiced': 'doc',
  'invoice.change.applied': 'check',
  'command.expired': 'hourglass',
  'command.failed': 'warn',
  'invoice.finalized': 'check',
  'invoice.deleted': 'trash',
  'manager.alias.orphaned': 'user',
  'legacy.message': 'megaphone',
  'price.personal.ready': 'tagPercent',
}

/** Текст по виду события — ключи словаря i18n (неймспейс `notifications`). */
const KIND_TEXT: Record<CodedKind, ParseKeys<'notifications'>> = {
  'manager.reserve.placed': 'kind.managerReservePlaced',
  'manager.invoice.appended': 'kind.managerInvoiceAppended',
  'manager.invoice.changed': 'kind.managerInvoiceChanged',
  'client.invoice.changed': 'kind.clientInvoiceChanged',
  'reserve.invoiced': 'kind.reserveInvoiced',
  'invoice.change.applied': 'kind.invoiceChangeApplied',
  'command.expired': 'kind.commandExpired',
  'command.failed': 'kind.commandFailed',
  'invoice.finalized': 'kind.invoiceFinalized',
  'invoice.deleted': 'kind.invoiceDeleted',
  'manager.alias.orphaned': 'kind.managerAliasOrphaned',
  'price.personal.ready': 'kind.pricePersonalReady',
}

/** Уточнение правки счёта — отдельным словом, не склейкой во фразу. */
const INVOICE_CHANGE_TEXT: Record<InvoiceChangeView, ParseKeys<'notifications'>> = {
  quantity: 'invoiceChange.quantity',
  remove: 'invoiceChange.remove',
  comment: 'invoiceChange.comment',
  email: 'invoiceChange.email',
  'write-off': 'invoiceChange.writeOff',
  confirm: 'invoiceChange.confirm',
  order: 'invoiceChange.order',
}

/**
 * Одна строка ленты уведомлений: глиф вида, текст (по `kind` из словаря или строки 1С для
 * `legacy.message` — текстом, без тегов), время и пометка «не прочитано». Карточка
 * презентационная: мутацию «прочитано» приносит слот `action`. Используется и страницей, и
 * лентой колокольчика в шапке.
 */
export function NotificationItemCard({ item, action }: NotificationItemCardProps) {
  const { t } = useTranslation('notifications')
  const content = describeNotification(item)
  const unread = item.readAt === null

  return (
    <article className={cx(s.root, unread && s.unread)} data-unread={unread ? 'true' : undefined}>
      <span className={s.icon} aria-hidden="true">
        <Icon name={KIND_ICON[item.kind]} size="sm" />
      </span>

      <div className={s.body}>
        <div className={s.text}>
          {content.variant === 'legacy' ? (
            content.lines.length > 0 ? (
              content.lines.map((line, index) => (
                <span key={index} className={s.line}>
                  {line}
                </span>
              ))
            ) : (
              <span className={s.line}>{t('kind.legacyEmpty')}</span>
            )
          ) : (
            <>
              <span className={s.line}>{t(KIND_TEXT[content.kind])}</span>
              {content.detail !== null ? (
                <span className={s.detail}>{t(INVOICE_CHANGE_TEXT[content.detail])}</span>
              ) : null}
            </>
          )}
        </div>

        <div className={s.meta}>
          <time className={s.time} dateTime={item.createdAt}>
            {formatDateTime(item.createdAt)}
          </time>
          {unread ? (
            <StatusBadge tone="accent" size="xs" indicator="dot">
              {t('unread')}
            </StatusBadge>
          ) : null}
        </div>
      </div>

      {action !== undefined ? <div className={s.action}>{action}</div> : null}
    </article>
  )
}
